import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import supabase from '../../../config/supabaseClient';
import { useAuth } from '../../../modules/shared/context/AuthContext';

// Criar o contexto
const ObrasContext = createContext(null);

// Hook personalizado para usar o contexto
export const useObras = () => {
  const context = useContext(ObrasContext);
  if (!context) {
    throw new Error('useObras deve ser usado dentro de um ObrasProvider');
  }
  return context;
};

// Componente Provider
export const ObrasProvider = ({ children }) => {
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, checkAndRefreshToken } = useAuth();

  // Carregar obras do Supabase quando o usuário estiver autenticado
  useEffect(() => {
    const fetchObras = async () => {
      if (!user) {
        setObras([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Verificar e renovar token se necessário
        await checkAndRefreshToken();
        
        // Buscar todas as obras
        const { data: obrasData, error: obrasError } = await supabase
          .from('obras')
          .select('*');
        
        if (obrasError) throw obrasError;
        
        // Para cada obra, buscar seus trechos e itens
        const obrasCompletas = await Promise.all(obrasData.map(async (obra) => {
          // Buscar trechos da obra
          const { data: trechosData } = await supabase
            .from('trechos')
            .select('*')
            .eq('obra_id', obra.id);
          
          // Buscar derivações para cada trecho
          const trechosComDerivacoes = await Promise.all((trechosData || []).map(async (trecho) => {
            const { data: derivacoesData } = await supabase
              .from('derivacoes')
              .select('*')
              .eq('trecho_id', trecho.id);
            
            return {
              id: trecho.id,
              nome: trecho.nome,
              tipo: trecho.tipo,
              obra_id: trecho.obra_id,
              distanciaMetros: trecho.distancia_metros,
              pontos: trecho.pontos ? JSON.parse(trecho.pontos) : [],
              derivacoes: (derivacoesData || []).map(d => ({
                id: d.id,
                nome: d.nome,
                pontoOrigem: d.ponto_origem,
                trecho_id: d.trecho_id,
                distanciaMetros: d.distancia_metros,
                pontos: d.pontos ? JSON.parse(d.pontos) : []
              }))
            };
          }));
          
          // Buscar itens da obra
          const { data: itensData } = await supabase
            .from('itens')
            .select('*')
            .eq('obra_id', obra.id);
          
          // Buscar detalhes para cada item
          const itensComDetalhes = await Promise.all((itensData || []).map(async (item) => {
            const { data: detalhesData } = await supabase
              .from('detalhes')
              .select('*')
              .eq('item_id', item.id);
            
            // Mapear campos para o formato esperado pela aplicação
            return {
              ...item,
              quantidadeMedida: item.quantidade_medida,
              totalMedido: item.total_medido,
              detalhes: detalhesData || []
            };
          }));
          
          // Retornar obra completa com trechos e itens, convertendo snake_case para camelCase
          return {
            id: obra.id,
            nome: obra.nome,
            empreiteiro: obra.empreiteiro,
            valor: obra.valor,
            status: obra.status,
            dataInicio: obra.data_inicio,
            dataPrevisaoTermino: obra.data_previsao_termino,
            observacoes: obra.observacoes,
            contrato: obra.contrato,
            licitacao: obra.licitacao,
            processo: obra.processo,
            gestor: obra.gestor,
            fiscaisObras: obra.fiscais_obras,
            totalMedicoes: obra.total_medicoes,
            trechos: trechosComDerivacoes || [],
            itens: itensComDetalhes || []
          };
        }));
        
        setObras(obrasCompletas);
        setError(null);
      } catch (error) {
        console.error('Erro ao carregar obras:', error);
        setError(error.message);
        toast.error('Erro ao carregar dados das obras');
      } finally {
        setLoading(false);
      }
    };

    fetchObras();
  }, [user, checkAndRefreshToken]);

  // Adicionar uma nova obra
  const adicionarObra = async (novaObra) => {
    if (!user) {
      toast.error('Você precisa estar autenticado para realizar esta ação');
      return null;
    }

    setLoading(true);
    try {
      // Verificar e renovar token se necessário
      await checkAndRefreshToken();
      
      // Extrair trechos e itens para inserir separadamente
      const { trechos = [], itens = [], ...dadosObra } = novaObra;
      
      // Inserir a obra principal
      const { data: obraInserida, error: obraError } = await supabase
        .from('obras')
        .insert([{
          nome: dadosObra.nome,
          empreiteiro: dadosObra.empreiteiro,
          valor: parseFloat(dadosObra.valor),
          status: dadosObra.status,
          data_inicio: dadosObra.dataInicio,
          data_previsao_termino: dadosObra.dataPrevisaoTermino,
          observacoes: dadosObra.observacoes,
          contrato: dadosObra.contrato,
          licitacao: dadosObra.licitacao,
          processo: dadosObra.processo,
          gestor: dadosObra.gestor,
          fiscais_obras: dadosObra.fiscaisObras,
          total_medicoes: parseFloat(dadosObra.totalMedicoes || 0),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select();
      
      if (obraError) throw obraError;
      
      const obraId = obraInserida[0].id;
      let trechosInseridos = [];
      let itensInseridos = [];
      
      // Inserir trechos
      if (trechos && trechos.length > 0) {
        for (const trecho of trechos) {
          const { derivacoes = [], ...dadosTrecho } = trecho;
          
          // Inserir trecho
          const { data: trechoInserido, error: trechoError } = await supabase
            .from('trechos')
            .insert([{ 
              nome: dadosTrecho.nome,
              tipo: dadosTrecho.tipo,
              obra_id: obraId,
              pontos: JSON.stringify(dadosTrecho.pontos),
              distancia_metros: dadosTrecho.distanciaMetros || null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }])
            .select();
          
          if (trechoError) throw trechoError;
          
          const trechoId = trechoInserido[0].id;
          let derivacoesInseridas = [];
          
          // Inserir derivações do trecho
          if (derivacoes && derivacoes.length > 0) {
            for (const derivacao of derivacoes) {
              const { data: derivacaoInserida, error: derivacaoError } = await supabase
                .from('derivacoes')
                .insert([{ 
                  nome: derivacao.nome,
                  ponto_origem: derivacao.pontoOrigem,
                  trecho_id: trechoId,
                  pontos: JSON.stringify(derivacao.pontos),
                  distancia_metros: derivacao.distanciaMetros || null,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                }])
                .select();
              
              if (derivacaoError) throw derivacaoError;
              derivacoesInseridas.push({
                id: derivacaoInserida[0].id,
                nome: derivacaoInserida[0].nome,
                pontoOrigem: derivacaoInserida[0].ponto_origem,
                trecho_id: derivacaoInserida[0].trecho_id,
                distanciaMetros: derivacaoInserida[0].distancia_metros,
                pontos: JSON.parse(derivacaoInserida[0].pontos)
              });
            }
          }
          
          trechosInseridos.push({
            id: trechoInserido[0].id,
            nome: trechoInserido[0].nome,
            tipo: trechoInserido[0].tipo,
            obra_id: trechoInserido[0].obra_id,
            distanciaMetros: trechoInserido[0].distancia_metros,
            pontos: JSON.parse(trechoInserido[0].pontos),
            derivacoes: derivacoesInseridas
          });
        }
      }
      
      // Inserir itens
      if (itens && itens.length > 0) {
        for (const item of itens) {
          const { detalhes = [], ...dadosItem } = item;
          
          // Inserir item
          const { data: itemInserido, error: itemError } = await supabase
            .from('itens')
            .insert([{ 
              item: dadosItem.item,
              descricao: dadosItem.descricao,
              unidade: dadosItem.unidade,
              obra_id: obraId,
              preco: parseFloat(dadosItem.preco),
              quantidade: parseFloat(dadosItem.quantidade),
              total: parseFloat(dadosItem.total),
              quantidade_medida: parseFloat(dadosItem.quantidadeMedida || 0),
              total_medido: parseFloat(dadosItem.totalMedido || 0),
              progresso: parseFloat(dadosItem.progresso || 0),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }])
            .select();
          
          if (itemError) throw itemError;
          
          const itemId = itemInserido[0].id;
          let detalhesInseridos = [];
          
          // Inserir detalhes do item
          if (detalhes && detalhes.length > 0) {
            for (const detalhe of detalhes) {
              const { data: detalheInserido, error: detalheError } = await supabase
                .from('detalhes')
                .insert([{ 
                  data: detalhe.data,
                  operacao: detalhe.operacao,
                  numero_medicao: detalhe.numeroMedicao,
                  descricao_medicao: detalhe.descricaoMedicao,
                  item_id: itemId,
                  quantidade: parseFloat(detalhe.quantidade),
                  valor: parseFloat(detalhe.valor),
                  total: parseFloat(detalhe.total),
                  saldo_quantidade: parseFloat(detalhe.saldoQuantidade || 0),
                  saldo_valor: parseFloat(detalhe.saldoValor || 0),
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                }])
                .select();
              
              if (detalheError) throw detalheError;
              detalhesInseridos.push(detalheInserido[0]);
            }
          }
          
          itensInseridos.push({
            ...itemInserido[0],
            detalhes: detalhesInseridos
          });
        }
      }
      
      // Construir a obra completa com os dados inseridos, convertendo snake_case para camelCase
      const obraCompleta = {
        id: obraInserida[0].id,
        nome: obraInserida[0].nome,
        empreiteiro: obraInserida[0].empreiteiro,
        valor: obraInserida[0].valor,
        status: obraInserida[0].status,
        dataInicio: obraInserida[0].data_inicio,
        dataPrevisaoTermino: obraInserida[0].data_previsao_termino,
        observacoes: obraInserida[0].observacoes,
        contrato: obraInserida[0].contrato,
        licitacao: obraInserida[0].licitacao,
        processo: obraInserida[0].processo,
        gestor: obraInserida[0].gestor,
        fiscaisObras: obraInserida[0].fiscais_obras,
        totalMedicoes: obraInserida[0].total_medicoes,
        trechos: trechosInseridos,
        itens: itensInseridos
      };
      
      // Atualizar o estado local
      setObras(prevObras => [...prevObras, obraCompleta]);
      
      toast.success('Obra cadastrada com sucesso!');
      return obraCompleta;
    } catch (error) {
      console.error('Erro ao cadastrar obra:', error);
      toast.error('Erro ao cadastrar obra: ' + error.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Atualizar uma obra existente
  const atualizarObra = async (id, dadosAtualizados) => {
    if (!user) {
      toast.error('Você precisa estar autenticado para realizar esta ação');
      return false;
    }

    setLoading(true);
    try {
      // Verificar e renovar token se necessário
      await checkAndRefreshToken();
      
      // Extrair trechos e itens para atualizar separadamente
      const { trechos = [], itens = [], ...dadosObra } = dadosAtualizados;
      
      // Atualizar a obra principal
      const { error: obraError } = await supabase
        .from('obras')
        .update({
          nome: dadosObra.nome,
          empreiteiro: dadosObra.empreiteiro,
          valor: parseFloat(dadosObra.valor),
          status: dadosObra.status,
          data_inicio: dadosObra.dataInicio,
          data_previsao_termino: dadosObra.dataPrevisaoTermino,
          observacoes: dadosObra.observacoes,
          contrato: dadosObra.contrato,
          licitacao: dadosObra.licitacao,
          processo: dadosObra.processo,
          gestor: dadosObra.gestor,
          fiscais_obras: dadosObra.fiscaisObras,
          total_medicoes: parseFloat(dadosObra.totalMedicoes || 0),
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
      
      if (obraError) throw obraError;
      
      // Atualizar trechos
      if (trechos && trechos.length > 0) {
        // Primeiro, excluir todos os trechos existentes (e suas derivações em cascata)
        const { error: deleteError } = await supabase
          .from('trechos')
          .delete()
          .eq('obra_id', id);
        
        if (deleteError) throw deleteError;
        
        // Inserir os novos trechos
        for (const trecho of trechos) {
          const { derivacoes = [], ...dadosTrecho } = trecho;
          
          // Inserir trecho
          const { data: trechoInserido, error: trechoError } = await supabase
            .from('trechos')
            .insert([{ 
              nome: dadosTrecho.nome,
              tipo: dadosTrecho.tipo,
              obra_id: id,
              pontos: JSON.stringify(dadosTrecho.pontos),
              distancia_metros: dadosTrecho.distanciaMetros || null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }])
            .select();
          
          if (trechoError) throw trechoError;
          
          const trechoId = trechoInserido[0].id;
          
          // Inserir derivações do trecho
          if (derivacoes && derivacoes.length > 0) {
            for (const derivacao of derivacoes) {
              const { error: derivacaoError } = await supabase
                .from('derivacoes')
                .insert([{ 
                  nome: derivacao.nome,
                  ponto_origem: derivacao.pontoOrigem,
                  trecho_id: trechoId,
                  pontos: JSON.stringify(derivacao.pontos),
                  distancia_metros: derivacao.distanciaMetros || null,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                }]);
              
              if (derivacaoError) throw derivacaoError;
            }
          }
        }
      }
      
      // Atualizar itens
      if (itens && itens.length > 0) {
        // Primeiro, excluir todos os itens existentes (e seus detalhes em cascata)
        const { error: deleteItemsError } = await supabase
          .from('itens')
          .delete()
          .eq('obra_id', id);
        
        if (deleteItemsError) throw deleteItemsError;
        
        // Inserir os novos itens
        for (const item of itens) {
          const { detalhes = [], ...dadosItem } = item;
          
          // Inserir item
          const { data: itemInserido, error: itemError } = await supabase
            .from('itens')
            .insert([{ 
              item: dadosItem.item,
              descricao: dadosItem.descricao,
              unidade: dadosItem.unidade,
              obra_id: id,
              preco: parseFloat(dadosItem.preco),
              quantidade: parseFloat(dadosItem.quantidade),
              total: parseFloat(dadosItem.total),
              quantidade_medida: parseFloat(dadosItem.quantidadeMedida || 0),
              total_medido: parseFloat(dadosItem.totalMedido || 0),
              progresso: parseFloat(dadosItem.progresso || 0),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }])
            .select();
          
          if (itemError) throw itemError;
          
          const itemId = itemInserido[0].id;
          
          // Inserir detalhes do item
          if (detalhes && detalhes.length > 0) {
            for (const detalhe of detalhes) {
              const { error: detalheError } = await supabase
                .from('detalhes')
                .insert([{ 
                  data: detalhe.data,
                  operacao: detalhe.operacao,
                  numero_medicao: detalhe.numeroMedicao,
                  descricao_medicao: detalhe.descricaoMedicao,
                  item_id: itemId,
                  quantidade: parseFloat(detalhe.quantidade),
                  valor: parseFloat(detalhe.valor),
                  total: parseFloat(detalhe.total),
                  saldo_quantidade: parseFloat(detalhe.saldoQuantidade || 0),
                  saldo_valor: parseFloat(detalhe.saldoValor || 0),
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                }]);
              
              if (detalheError) throw detalheError;
            }
          }
        }
      }
      
      // Buscar a obra atualizada completa
      const { data: obraAtualizada, error: fetchError } = await supabase
        .from('obras')
        .select('*')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Buscar trechos da obra
      const { data: trechosData } = await supabase
        .from('trechos')
        .select('*')
        .eq('obra_id', id);
      
      // Buscar derivações para cada trecho
      const trechosComDerivacoes = await Promise.all((trechosData || []).map(async (trecho) => {
        const { data: derivacoesData } = await supabase
          .from('derivacoes')
          .select('*')
          .eq('trecho_id', trecho.id);
        
        return {
          id: trecho.id,
          nome: trecho.nome,
          tipo: trecho.tipo,
          obra_id: trecho.obra_id,
          distanciaMetros: trecho.distancia_metros,
          pontos: trecho.pontos ? JSON.parse(trecho.pontos) : [],
          derivacoes: (derivacoesData || []).map(d => ({
            id: d.id,
            nome: d.nome,
            pontoOrigem: d.ponto_origem,
            trecho_id: d.trecho_id,
            distanciaMetros: d.distancia_metros,
            pontos: d.pontos ? JSON.parse(d.pontos) : []
          }))
        };
      }));
      
      // Buscar itens da obra
      const { data: itensData } = await supabase
        .from('itens')
        .select('*')
        .eq('obra_id', id);
      
      // Buscar detalhes para cada item
      const itensComDetalhes = await Promise.all((itensData || []).map(async (item) => {
        const { data: detalhesData } = await supabase
          .from('detalhes')
          .select('*')
          .eq('item_id', item.id);
        
        // Mapear campos para o formato esperado pela aplicação
        return {
          ...item,
          quantidadeMedida: item.quantidade_medida,
          totalMedido: item.total_medido,
          detalhes: detalhesData || []
        };
      }));
      
      // Construir a obra completa com os dados atualizados, convertendo snake_case para camelCase
      const obraCompletaAtualizada = {
        id: obraAtualizada.id,
        nome: obraAtualizada.nome,
        empreiteiro: obraAtualizada.empreiteiro,
        valor: obraAtualizada.valor,
        status: obraAtualizada.status,
        dataInicio: obraAtualizada.data_inicio,
        dataPrevisaoTermino: obraAtualizada.data_previsao_termino,
        observacoes: obraAtualizada.observacoes,
        contrato: obraAtualizada.contrato,
        licitacao: obraAtualizada.licitacao,
        processo: obraAtualizada.processo,
        gestor: obraAtualizada.gestor,
        fiscaisObras: obraAtualizada.fiscais_obras,
        totalMedicoes: obraAtualizada.total_medicoes,
        trechos: trechosComDerivacoes || [],
        itens: itensComDetalhes || []
      };
      
      // Atualizar o estado local
      setObras(prevObras => 
        prevObras.map(obra => 
          obra.id === parseInt(id) ? obraCompletaAtualizada : obra
        )
      );
      
      toast.success('Obra atualizada com sucesso!');
      return true;
    } catch (error) {
      console.error('Erro ao atualizar obra:', error);
      toast.error('Erro ao atualizar obra: ' + error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Excluir uma obra
  const excluirObra = async (id) => {
    if (!user) {
      toast.error('Você precisa estar autenticado para realizar esta ação');
      return false;
    }

    setLoading(true);
    try {
      // Verificar e renovar token se necessário
      await checkAndRefreshToken();
      
      // Excluir a obra (as tabelas relacionadas devem ter ON DELETE CASCADE)
      const { error } = await supabase
        .from('obras')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // Atualizar o estado local
      setObras(prevObras => prevObras.filter(obra => obra.id !== parseInt(id)));
      
      toast.success('Obra excluída com sucesso!');
      return true;
    } catch (error) {
      console.error('Erro ao excluir obra:', error);
      toast.error('Erro ao excluir obra: ' + error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Obter obra por ID
  const obterObraPorId = (id) => {
    return obras.find(obra => obra.id === parseInt(id));
  };

  // Filtrar obras
  const filtrarObras = (filtros) => {
    return obras.filter(obra => {
      const termoBusca = filtros.termo ? filtros.termo.toLowerCase() : '';
      const matchTermo = !termoBusca || 
        obra.nome.toLowerCase().includes(termoBusca) || 
        obra.empreiteiro.toLowerCase().includes(termoBusca);
      
      const matchStatus = !filtros.status || obra.status === filtros.status;
      
      return matchTermo && matchStatus;
    });
  };

  const value = {
    obras,
    loading,
    error,
    adicionarObra,
    atualizarObra,
    excluirObra,
    obterObraPorId,
    filtrarObras
  };

  return (
    <ObrasContext.Provider value={value}>
      {children}
    </ObrasContext.Provider>
  );
};